/**
 * Currency formatting utilities
 */

export interface FormatCurrencyOptions {
  currency?: string;
  compact?: boolean;
  hidden?: boolean;
  /** Drop the cents fraction. Useful for headline KPIs where two decimal places add no information. */
  noCents?: boolean;
}

// Intl.NumberFormat throws RangeError for anything that is not a well-formed
// ISO 4217 code (three letters), and upstream transaction data has carried the
// display symbol itself — e.g. `currencyCode: "R$"` — into this call in
// production. Malformed codes take a manual path below instead of crashing.
const ISO_CURRENCY_PATTERN = /^[A-Za-z]{3}$/;

/** "R$ 1.234,56"-style output for a currency Intl refuses: the raw string is
 *  already the user-facing symbol, so prefix it the way pt-BR prefixes BRL. */
function formatWithRawSymbol(
  value: number,
  symbol: string,
  { hidden, fractionDigits }: { hidden: boolean; fractionDigits: number },
): string {
  const prefix = value < 0 ? '-\u00a0' : '';
  if (hidden) return `${prefix}${symbol}\u00a0•••••`;
  const digits = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Math.abs(value));
  return `${prefix}${symbol}\u00a0${digits}`;
}

/**
 * Format a number as currency with 2 decimal places
 * @param value - The numeric value to format
 * @param options - Formatting options
 * @returns Formatted currency string (e.g., "R$ 1.234,56")
 */
export function formatCurrency(
  value: number,
  options: FormatCurrencyOptions = {}
): string {
  const { currency = 'BRL', compact = false, hidden = false, noCents = false } = options;
  const fractionDigits = noCents ? 0 : 2;

  if (!ISO_CURRENCY_PATTERN.test(currency)) {
    return formatWithRawSymbol(value, currency.trim() || 'R$', { hidden, fractionDigits });
  }

  if (hidden) {
    const prefix = value < 0 ? '-\u00A0' : '';
    const symbol = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).formatToParts(0).find((part) => part.type === 'currency')?.value ?? currency;
    return `${prefix}${symbol}\u00A0•••••`;
  }

  if (compact && Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
      notation: 'compact',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

const compactNumberFmt = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const exactNumberFmt = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * BRL formatter with compact notation for large values. Mirrors the chart
 * label heuristic so the strip and treemap stay visually consistent.
 *
 * - `>= 1_000_000` → "R$ 1,23 M"
 * - `>= 10_000`    → "R$ 12,3 mil"
 * - otherwise      → "R$ 9.876,54" (full precision)
 *
 * The 10k cutoff keeps 4-digit values precise; only 5-digit+ shorten.
 */
export function compactBrl(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `R$ ${compactNumberFmt.format(value / 1_000_000)} M`;
  if (abs >= 10_000) return `R$ ${compactNumberFmt.format(value / 1_000)} mil`;
  return `R$ ${exactNumberFmt.format(value)}`;
}
